import React from 'react';
import { BuildingIcon, CameraIcon, CheckCircle2Icon, ClipboardCheckIcon, EyeIcon, EyeOffIcon, FileTextIcon, FolderIcon, UserIcon } from 'lucide-react';
import { QualityReport } from '../../types/quality';
import { getStats } from '../../utils/quality';
import { ProgressRing } from './ProgressRing';
interface ContactField {
  key: string;
  label: string;
  value: string;
  placeholder: string;
  type?: string;
}
interface ContactCardProps {
  label: string;
  icon: typeof UserIcon;
  fields: ContactField[];
  onChange: (key: string, value: string) => void;
}

/** Editable contact block — these values are printed on the sent report. */
function ContactCard({
  label,
  icon: Icon,
  fields,
  onChange
}: ContactCardProps) {
  return <div className="rounded-2xl border border-stone-200 bg-white p-5" data-id="element-741" data-mp="1aoc84aorb5bw">
      <div className="flex items-center gap-2.5" data-id="element-742" data-mp="3qg9rjjplwmvn">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-500" data-id="element-743" data-mp="3qjntiqucyu2k">
          <Icon className="h-[18px] w-[18px]" data-id="element-744" data-mp="29bwfczzlglrn" />
        </span>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-745" data-mp="29eio32y817bg">
          {label}
        </p>
      </div>

      <div className="mt-4 space-y-3" data-id="element-746" data-mp="2jac5azmvxozk">
        {fields.map(field => <label key={field.key} className="block" data-id="element-747" data-mp="2j899u02yr3ng">
            <span className="mb-1 block text-xs font-medium text-stone-500" data-id="element-748" data-mp="2eahkndtpdvmy">
              {field.label}
            </span>
            <input type={field.type ?? 'text'} value={field.value} onChange={e => onChange(field.key, e.target.value)} placeholder={field.placeholder} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm text-stone-900 outline-none transition-colors placeholder:text-stone-400 focus:border-transparent focus:ring-2 focus:ring-brand-500" data-id="element-749" data-mp="2s9h7cfxno0gh" />
          </label>)}
      </div>
    </div>;
}
interface DetailsTabProps {
  report: QualityReport;
  onToggleVisibility: () => void;
  onDescriptionChange: (description: string) => void;
  onResponsibleChange: (responsible: QualityReport['responsible']) => void;
  onCompanyChange: (company: QualityReport['company']) => void;
  onChange: (changes: Partial<QualityReport>) => void;
}
export function DetailsTab({
  report,
  onToggleVisibility,
  onDescriptionChange,
  onResponsibleChange,
  onCompanyChange,
  onChange
}: DetailsTabProps) {
  const stats = getStats(report);
  const isDone = stats.percent === 100;
  const links = [{
    label: 'Kunde',
    icon: UserIcon,
    title: report.customer.name,
    meta: report.customer.address
  }, {
    label: 'Tilbud',
    icon: FileTextIcon,
    title: report.quote.reference,
    meta: `${report.quote.amount.toLocaleString('da-DK')} kr · ekskl. moms`,
    badge: report.quote.status
  }, {
    label: 'Projekt',
    icon: FolderIcon,
    title: report.project.title,
    meta: report.project.reference
  }];
  return <div className="space-y-5" data-id="element-750" data-mp="29uo3a6qqrz29">
      {/* Progress */}
      <div className="rounded-2xl border border-stone-200 bg-white p-5" data-id="element-751" data-mp="252wcbv3obrbl">
        <div className="flex items-center gap-4" data-id="element-752" data-mp="251l9bxi1ynlj">
          <ProgressRing percent={stats.percent} size={60} data-id="element-753" data-mp="2039vlbomzsy1" />
          <div className="min-w-0" data-id="element-754" data-mp="201p09jui05z4">
            <h3 className="text-base font-semibold text-stone-900" data-id="element-755" data-mp="1v58egwaru3p7">
              {isDone ? 'Kvalitetssikringen er færdig' : 'Færdiggør kvalitetssikringen'}
            </h3>
            <p className="mt-0.5 text-sm text-stone-500" data-id="element-756" data-mp="2nt4by385zzn1">
              {isDone ? <>
                  Alle {stats.totalPoints} kontrolpunkter er registreret og
                  dokumenteret.
                </> : stats.missingPhoto > 0 ? <>
                  {stats.missingPhoto}{' '}
                  {stats.missingPhoto === 1 ? 'kontrolpunkt mangler' : 'kontrolpunkter mangler'}{' '}
                  foto. Fotografér før det bliver dækket til.
                </> : <>
                  {stats.completedPoints} af {stats.totalPoints} kontrolpunkter
                  er registreret.
                </>}
            </p>
          </div>
        </div>
      </div>

      {/* Visibility */}
      <button onClick={onToggleVisibility} aria-pressed={report.visibleToCustomer} className="flex w-full items-center gap-3 rounded-2xl border border-stone-200 bg-white p-5 text-left transition-colors hover:bg-stone-50" data-id="element-757" data-mp="2mbnkj6fua1l6">
        <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${report.visibleToCustomer ? 'bg-emerald-50 text-emerald-600' : 'bg-stone-100 text-stone-400'}`} data-id="element-758" data-mp="30322rb1x9yr6">
          {report.visibleToCustomer ? <EyeIcon className="h-5 w-5" data-id="element-759" data-mp="1tmqcdw5cdl65" /> : <EyeOffIcon className="h-5 w-5" data-id="element-760" data-mp="1tk3t4z939h83" />}
        </span>
        <span className="min-w-0 flex-1" data-id="element-761" data-mp="bna5ns5sfcps">
          <span className="block text-base font-semibold text-stone-900" data-id="element-762" data-mp="6pqtcxsnha0h">
            Synligt for kunden
          </span>
          <span className="mt-0.5 block text-sm text-stone-500" data-id="element-763" data-mp="kpt84b5nvnbi">
            {report.visibleToCustomer ? 'Kunden kan se rapporten og de godkendte kontrolpunkter.' : 'Rapporten er kun synlig internt.'}
          </span>
        </span>
        <span className={`relative h-6 w-11 flex-shrink-0 rounded-full transition-colors ${report.visibleToCustomer ? 'bg-emerald-500' : 'bg-stone-300'}`} data-id="element-764" data-mp="3p1dk2pd8a2w5">
          <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm" style={{
          left: report.visibleToCustomer ? 22 : 2,
          transition: 'left 160ms cubic-bezier(0.23, 1, 0.32, 1)'
        }} data-id="element-765" data-mp="1siaeuzoh1y6" />
        </span>
      </button>

      {/* Photos are optional unless documentation is part of the deliverable */}
      <button onClick={() => onChange({
      requirePhotos: !report.requirePhotos
    })} aria-pressed={Boolean(report.requirePhotos)} className="flex w-full items-center gap-3 rounded-2xl border border-stone-200 bg-white p-5 text-left transition-colors hover:bg-stone-50" data-id="element-766" data-mp="14d9c7p2m9eq4">
        <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${report.requirePhotos ? 'bg-brand-50 text-brand-600' : 'bg-stone-100 text-stone-400'}`} data-id="element-767" data-mp="1j1nn8na6s9hr">
          <CameraIcon className="h-5 w-5" data-id="element-768" data-mp="vcl8poaumwrh" />
        </span>
        <span className="min-w-0 flex-1" data-id="element-769" data-mp="vexl7lxu8ex5">
          <span className="block text-base font-semibold text-stone-900" data-id="element-770" data-mp="10c7ljwmrmazr">
            Kræv foto på kontrolpunkter
          </span>
          <span className="mt-0.5 block text-sm text-stone-500" data-id="element-771" data-mp="26rhy4zay3nzd">
            {report.requirePhotos ? 'Et kontrolpunkt tæller først som færdigt når der er et foto på.' : 'Fotos er valgfrie — du kan tilføje dem, men de blokerer ikke.'}
          </span>
        </span>
        <span className={`relative h-6 w-11 flex-shrink-0 rounded-full transition-colors ${report.requirePhotos ? 'bg-brand-500' : 'bg-stone-300'}`} data-id="element-772" data-mp="3rbdfcrhyrf3o">
          <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm" style={{
          left: report.requirePhotos ? 22 : 2,
          transition: 'left 160ms cubic-bezier(0.23, 1, 0.32, 1)'
        }} data-id="element-773" data-mp="3hjg1fczkm919" />
        </span>
      </button>

      {/* KS info */}
      <div className="rounded-2xl border border-stone-200 bg-white p-5" data-id="element-774" data-mp="33kq3xw8ua7cd">
        <div className="flex items-start gap-3" data-id="element-775" data-mp="2ymept9bihaoc">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-500" data-id="element-776" data-mp="2ynpst6x4ueee">
            <ClipboardCheckIcon className="h-5 w-5" data-id="element-777" data-mp="wtiavbehdnyy" />
          </span>
          <div className="min-w-0" data-id="element-778" data-mp="ws773bl1cg7u">
            <h3 className="text-base font-semibold text-stone-900" data-id="element-779" data-mp="5oe8wa3hig0y">KS-info</h3>
            <p className="text-sm text-stone-500" data-id="element-780" data-mp="anhd5sbn4i7v">Hvad rapporten handler om</p>
            <textarea value={report.description} onChange={e => onDescriptionChange(e.target.value)} rows={4} aria-label="Beskrivelse af kvalitetssikringen" placeholder="Beskriv hvad kvalitetssikringen dækker…" className="mt-3 w-full resize-y rounded-xl border border-stone-200 bg-stone-50 px-3 py-2.5 text-sm leading-relaxed text-stone-700 outline-none transition-colors placeholder:text-stone-400 focus:border-transparent focus:bg-white focus:ring-2 focus:ring-brand-500" data-id="element-781" data-mp="apleq3m2h54z" />
          </div>
        </div>
      </div>

      {/* Linked records */}
      <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white" data-id="element-782" data-mp="17drnn2q0ujp2">
        <ul className="divide-y divide-stone-100" data-id="element-783" data-mp="1cdmh9e16lndt">
          {links.map(link => {
          const Icon = link.icon;
          return <li key={link.label} className="flex items-center gap-3 px-5 py-4" data-id="element-784" data-mp="2wvevzmpv1swi">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-500" data-id="element-785" data-mp="36kydop1u7gps">
                  <Icon className="h-[18px] w-[18px]" data-id="element-786" data-mp="31tyh5kwmr0nt" />
                </span>
                <div className="min-w-0 flex-1" data-id="element-787" data-mp="3fld1pw66r9wz">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-788" data-mp="3fk20a307qe91">
                    {link.label}
                  </p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-2 text-sm font-medium text-stone-900" data-id="element-789" data-mp="3pkcdhnm8zlo7">
                    <span className="truncate" data-id="element-790" data-mp="3kkg7xjxwjm6j">{link.title}</span>
                    {link.badge && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700" data-id="element-791" data-mp="1v5032gr0gmmg">
                        <CheckCircle2Icon className="h-3 w-3" data-id="element-792" data-mp="1q8t10z1qjv9s" />
                        {link.badge}
                      </span>}
                  </p>
                  <p className="mt-0.5 truncate text-sm text-stone-500" data-id="element-793" data-mp="24rocsh9ufn7k">
                    {link.meta}
                  </p>
                </div>
              </li>;
        })}
        </ul>
      </div>

      {/* Responsible + company — editable, they appear on the sent report */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-794" data-mp="2shiq0zy2rdsc">
        <ContactCard label="Ansvarlig" icon={UserIcon} fields={[{
        key: 'name',
        label: 'Navn',
        value: report.responsible.name,
        placeholder: 'f.eks. Stefan Sørensen'
      }, {
        key: 'email',
        label: 'E-mail',
        value: report.responsible.email,
        placeholder: 'navn@lykke-el.dk',
        type: 'email'
      }, {
        key: 'phone',
        label: 'Telefon',
        value: report.responsible.phone,
        placeholder: '+45 00 00 00 00',
        type: 'tel'
      }]} onChange={(key, value) => onResponsibleChange({
        ...report.responsible,
        [key]: value
      })} data-id="element-795" data-mp="2nk0m5nywzor7" />

        <ContactCard label="Virksomhed" icon={BuildingIcon} fields={[{
        key: 'name',
        label: 'Navn',
        value: report.company.name,
        placeholder: 'Lykke-EL'
      }, {
        key: 'email',
        label: 'E-mail',
        value: report.company.email,
        placeholder: 'kontakt@lykke-el.dk',
        type: 'email'
      }, {
        key: 'website',
        label: 'Hjemmeside',
        value: report.company.website,
        placeholder: 'www.lykke-el.dk'
      }]} onChange={(key, value) => onCompanyChange({
        ...report.company,
        [key]: value
      })} data-id="element-796" data-mp="171b9g9p7u5bu" />
      </div>
    </div>;
}